// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { makeStyles, mergeClasses } from '@fluentui/react-components';
import React, { useContext } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { IntlProps, TrackContext, TrackedButton } from '../components/common';
import HeroCourses from '../components/course-card/hero-courses';
import { InAppFeedbackContext } from '../components/header/in-app-feedback';
import PageContainer from '../components/page-container';
import { SearchImage } from '../icons/status';
import { useAppStyles } from '../styles';

const useStyles = makeStyles({
    header: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        alignSelf: 'center',
        rowGap: '2rem',
        maxWidth: '32rem',
        textAlign: 'center',
    },
    image: {
        width: '8rem',
        height: '8rem',
    },
});

// Error page.
export const ErrorPage = injectIntl((_props: IntlProps) => {
    const appStyles = useAppStyles();
    const styles = useStyles();
    const navigate = useNavigate();
    const feedbackContext = useContext(InAppFeedbackContext);
    return (
        <TrackContext.Provider value={{ container: 'error' }}>
            <PageContainer className={appStyles.pageContainer}>
                <div className={mergeClasses(appStyles.pageContent, appStyles.section, 'content-width')}>
                    <div className={styles.header}>
                        <SearchImage className={styles.image} />
                        <h1 className={appStyles.sectionTitle}>
                            <FormattedMessage
                                defaultMessage="Oops! We cannot find that page"
                                description="Error page title."
                            />
                        </h1>
                        <FormattedMessage
                            defaultMessage="It looks like the page you are looking for does not exist. But don't worry, there's still plenty to explore and learn!"
                            description="Error page message."
                        />
                        <div className={appStyles.flexRowSmallGap}>
                            <TrackedButton trackAction="home-page" role="link" onClick={() => navigate('/')}>
                                <FormattedMessage defaultMessage="Home page" description="Error page button label." />
                            </TrackedButton>
                            <TrackedButton trackAction="report-problem" onClick={() => feedbackContext.launch()}>
                                <FormattedMessage
                                    defaultMessage="Report a problem"
                                    description="Error page button label."
                                />
                            </TrackedButton>
                            <TrackedButton
                                trackAction="explore-catalog"
                                role="link"
                                className={mergeClasses(appStyles.buttonMedium, appStyles.buttonPrimary)}
                                onClick={() => navigate('/courses')}
                            >
                                <FormattedMessage
                                    defaultMessage="Explore catalog"
                                    description="Error page button label."
                                />
                            </TrackedButton>
                        </div>
                        <div className={appStyles.sectionSubtitle}>
                            <FormattedMessage
                                defaultMessage="Check out these featured activities"
                                description="Error page subtitle title."
                            />
                        </div>
                    </div>
                    <HeroCourses />
                </div>
            </PageContainer>
        </TrackContext.Provider>
    );
});

export default ErrorPage;
